// Global activity log viewer — flat list of events across all grids/wind
// sessions/market guards. Click a row to toggle its JSON detail panel
// (mirrors the existing copy-prompt detail UX; no modal so the scroll
// context stays intact).
import { useState } from "react";
import { useActivity, type ActivityRow } from "@/hooks/use-activity";
import TimeAgo from "@/components/common/time-ago";

const CATEGORY_COLOR: Record<string, string> = {
  ai: "text-accent",
  wind: "text-success",
  grid: "text-success",
  order: "text-muted",
  exit: "text-warn",
  guard: "text-warn",
  market: "text-dim",
};

// Lightweight filter pills — empty string = all. Keeping the set small
// avoids cluttering mobile; less-common categories fall into "Other".
const FILTERS = [
  { key: "", label: "All" },
  { key: "ai", label: "AI" },
  { key: "wind", label: "Wind" },
  { key: "grid", label: "Grid" },
  { key: "guard", label: "Guard" },
] as const;

export default function ActivityPage() {
  const [filter, setFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<number | null>(null);
  // Filter is passed to the hook so each fetched page is PAGE_SIZE rows of
  // the selected category — no more jumpy counts after client-side filter.
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useActivity(filter || undefined);

  const rows = data?.pages.flat() ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-0.5">
        <h2 className="text-sm font-semibold text-text">
          Activity{" "}
          <span className="text-muted font-normal text-xs">
            {rows.length} events
          </span>
        </h2>
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center h-7 px-2.5 text-xs font-medium rounded border transition-colors ${
                filter === f.key
                  ? "border-accent/60 text-accent bg-accent/10"
                  : "border-border text-muted hover:text-text"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="text-muted text-sm text-center py-8">Loading…</div>
      )}
      {error && (
        <div className="text-danger text-sm text-center py-8">
          Failed to load activity
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="text-muted text-sm text-center py-8">
          No activity yet
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <>
          <ul className="space-y-1">
            {rows.map((r) => (
              <ActivityItem
                key={r.id}
                row={r}
                open={expanded === r.id}
                onToggle={() => setExpanded((cur) => (cur === r.id ? null : r.id))}
              />
            ))}
          </ul>
          {hasNextPage && (
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="inline-flex items-center h-7 px-4 text-xs font-medium rounded border border-border text-muted hover:text-text hover:border-dim disabled:opacity-50 disabled:cursor-wait transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
          {!hasNextPage && rows.length > 0 && (
            <div className="text-[0.7rem] text-dim text-center mt-3">
              End of log
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ActivityItem({
  row,
  open,
  onToggle,
}: {
  row: ActivityRow;
  open: boolean;
  onToggle: () => void;
}) {
  const catColor = CATEGORY_COLOR[row.category] ?? "text-muted";
  const hasDetail = !!row.detail;
  let pretty: string | null = null;
  if (hasDetail) {
    try {
      pretty = JSON.stringify(JSON.parse(row.detail!), null, 2);
    } catch {
      pretty = row.detail; // fallback to raw string
    }
  }

  return (
    <li className="border border-border/40 rounded bg-surface/40">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-surface/70 transition-colors"
      >
        <span className={`text-[0.68rem] uppercase font-semibold tracking-wider w-12 pt-0.5 ${catColor}`}>
          {row.category}
        </span>
        <span className="flex-1 min-w-0">
          <span className="text-sm text-text block break-words">{row.message}</span>
          <TimeAgo iso={row.created_at} className="text-[0.7rem] text-dim" />
        </span>
        {hasDetail && (
          <span className="text-[0.7rem] text-muted pt-0.5 select-none">
            {open ? "▾" : "▸"}
          </span>
        )}
      </button>
      {open && hasDetail && (
        <pre className="px-3 pb-2 text-[0.68rem] text-dim overflow-x-auto whitespace-pre-wrap break-all border-t border-border/30 bg-bg/40">
          {pretty}
        </pre>
      )}
    </li>
  );
}
